import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { getJourneySource } from './journeySource';
import { resolveSegmentRefs } from '../fixtures/segments';
import { deriveModel } from '../deriveTree';

// One source instance per module load (api vs fixtures resolved once).
let _source;
export function getSource() { if (!_source) _source = getJourneySource(); return _source; }

export const queryKeys = {
  journeyList: ['journeys', 'list'],
  attributes: ['attributes'],
  segments: (segmentIds) => ['segments', [...segmentIds].sort((a, b) => a - b)],
  touchpoints: (journeyId) => ['journeys', journeyId, 'touchpoints'],
};

export function useJourneyListQuery() {
  return useQuery(queryKeys.journeyList, ({ signal }) => getSource().listJourneys(signal));
}

export function useAttributesQuery() {
  return useQuery(queryKeys.attributes, ({ signal }) => getSource().attributeIndex(signal));
}

// segmentIds drives the key so the result is shared by every consumer needing
// the same set; pass the deduped entry segment_ids from the journey list.
export function useSegmentsQuery(segmentIds = []) {
  const ids = [...new Set(segmentIds.filter((id) => id != null))];
  return useQuery(queryKeys.segments(ids), ({ signal }) => getSource().segmentsFor(ids, signal), {
    enabled: ids.length > 0,
  });
}

export function useTouchpointsQuery(journeyId, enabled = true) {
  return useQuery(queryKeys.touchpoints(journeyId), ({ signal }) => getSource().listTouchpoints(journeyId, signal), {
    enabled: enabled && journeyId != null,
  });
}

// One touchpoints query per journey id (gallery count fill + sub-journey derive).
export function useTouchpointQueries(journeyIds = []) {
  return useQueries({
    queries: journeyIds.map((id) => ({
      queryKey: queryKeys.touchpoints(id),
      queryFn: ({ signal }) => getSource().listTouchpoints(id, signal),
    })),
  });
}

// BFS the trigger subtree of `entryId`: fetch the entry's touchpoints, collect the
// response attributes it produces, add any journey whose entry segment references one
// of those attributes, and recurse. Touchpoints are fetched ONLY for journeys in the
// subtree. `getTouchpoints(id) => Promise<Touchpoint[]>`.
export async function subtreeOf(entryId, allJourneys, segById, getTouchpoints) {
  const included = new Map();
  const produced = new Set();
  const queue = [entryId];
  const queued = new Set([entryId]);
  while (queue.length) {
    const jid = queue.shift();
    const j = allJourneys.find((x) => x.journey_id === jid);
    if (!j) continue;
    const touchpoints = await getTouchpoints(jid);
    included.set(jid, { ...j, touchpoints });
    touchpoints.forEach((tp) => {
      if (tp.response_attribute_id != null) produced.add(String(tp.response_attribute_id));
    });
    allJourneys.forEach((cand) => {
      if (included.has(cand.journey_id) || queued.has(cand.journey_id)) return;
      const refs = resolveSegmentRefs(segById.get(cand.segment_id), segById).map((r) => String(r.attribute_id));
      if (refs.some((r) => produced.has(r))) { queue.push(cand.journey_id); queued.add(cand.journey_id); }
    });
  }
  return [...included.values()];
}

// Assemble exactly the data deriveModel needs for one journey's timeline, reusing the
// shared cache (ensureQueryData) so the gallery's already-fetched list/segments/
// touchpoints are not re-requested.
export function useJourneyDetailQuery(journeyId) {
  const qc = useQueryClient();
  const id = Number(journeyId);
  return useQuery(['journeyDetail', id], async () => {
    const journeys = await qc.ensureQueryData(queryKeys.journeyList, () => getSource().listJourneys());
    const attributeById = await qc.ensureQueryData(queryKeys.attributes, () => getSource().attributeIndex());
    const segIds = [...new Set(journeys.map((j) => j.segment_id).filter((s) => s != null))];
    const segments = await qc.ensureQueryData(queryKeys.segments(segIds), () => getSource().segmentsFor(segIds));
    const segById = new Map(segments.map((s) => [s.segment_id, s]));
    const subtree = await subtreeOf(
      id, journeys, segById,
      (jid) => qc.ensureQueryData(queryKeys.touchpoints(jid), () => getSource().listTouchpoints(jid)),
    );
    return { journeys: subtree, segments, attributeById };
  }, { enabled: journeyId != null });
}

// Sub-journey count per journey: derive the subtree from each journey and subtract the
// root. Needs every journey's touchpoints (response attrs) and the entry segments.
export function subJourneyCounts(journeysWithTouchpoints, segments) {
  const out = {};
  journeysWithTouchpoints.forEach((j) => {
    out[j.journey_id] = deriveModel(j.journey_id, { journeys: journeysWithTouchpoints, segments }).journeys.length - 1;
  });
  return out;
}
